import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { RefreshCw as RefreshIcon, Save as SaveIcon, Zap as AutoIcon, X as ClearIcon } from 'lucide-react';
import { useEventEdition } from '../../contexts/EventEditionContext';
import {
  getBibAssignments,
  saveBibAssignments,
  autoAssignBibs,
  parseBibValue,
  BibAssignmentRow,
} from '../../services/bibAssignmentService';

const ASSIGNABLE_STATUSES = new Set(['pending', 'confirmed']);

const isAssignableRow = (row: BibAssignmentRow): boolean =>
  ASSIGNABLE_STATUSES.has(String(row.status).toLowerCase());

const isCancelledRow = (row: BibAssignmentRow): boolean =>
  String(row.status).toLowerCase() === 'cancelled';

const CLASS_LABELS: Record<string, string> = {
  // Generic class labels (KUTC style - using id/code)
  konkurranse: 'Konkurranse',
  trim_tidtaking: 'Trim m/tid',
  turklasse: 'Turklasse',
  competition: 'Konkurranse',
  trim: 'Trim m/tid',
  tur: 'Turklasse',
  // MO event class labels (from event.raceDistances.id)
  'MO-Comp': 'MO-Comp (Konkurranse)',
  'MO-Time': 'MO-Time (Trim m/tid)',
  'MO-Hike': 'MO-Hike (Tur)',
  // MO registration raceDistance values (using displayName_en - temporary workaround)
  Competition: 'Konkurranse',
  'Timed Recreation': 'Trim m/tid',
  Hiking: 'Turklasse',
};

const BibAssignmentPanel: React.FC = () => {
  const { event: selectedEvent } = useEventEdition();

  const [rows, setRows] = useState<BibAssignmentRow[]>([]);
  const [editedBibs, setEditedBibs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [startBib, setStartBib] = useState<string>('1');
  const [selectedClasses, setSelectedClasses] = useState<Set<string>>(new Set());
  const [allRows, setAllRows] = useState<BibAssignmentRow[]>([]); // Store all rows unfiltered

  // Get available race distances from actual registration data
  const availableClasses = useMemo(() => {
    // Extract unique raceDistance values from all loaded registrations
    const uniqueClasses = new Set(allRows.map((r) => r.raceDistance).filter(Boolean));
    return Array.from(uniqueClasses).sort();
  }, [allRows]);

  // Get display labels from event.raceDistances
  const getClassLabel = useCallback((classId: string): string => {
    if (!selectedEvent?.raceDistances) return CLASS_LABELS[classId] || classId;
    const normalized = String(classId || '').toLowerCase().replace(/[\s_-]/g, '');
    const mappedId =
      normalized === 'konkurranse' || normalized === 'competition'
        ? 'MO-Comp'
        : normalized === 'timedrecreation' || normalized === 'trimtidtaking' || normalized === 'trimmedtidtaking'
          ? 'MO-Time'
          : normalized === 'hiking' || normalized === 'hikingclass' || normalized === 'turklasse' || normalized === 'tur'
            ? 'MO-Hike'
            : classId;
    const raceDist = selectedEvent.raceDistances.find((rd: any) => 
      rd.id === classId || 
      rd.id === mappedId ||
      rd.code === classId || 
      rd.displayName_en === classId ||
      rd.displayName_no === classId ||
      rd.displayName === classId
    );
    // Return displayName_en if available, otherwise fallback
    return raceDist?.displayName_en || raceDist?.displayName || CLASS_LABELS[classId] || classId;
  }, [selectedEvent?.raceDistances]);

  // Initialize selected classes when data loads
  useEffect(() => {
    if (availableClasses.length > 0 && selectedClasses.size === 0) {
      // Default: select all classes EXCEPT hiking variants
      // This allows user to see hiking participants but they won't get bibs by default
      const defaultSelected = new Set(
        availableClasses.filter((cls) => 
          cls !== 'hiking' && cls !== 'tur' && cls !== 'MO-Hike' && cls !== 'Hiking'
        )
      );
      setSelectedClasses(defaultSelected);
    }
  }, [availableClasses, selectedClasses.size]);

  const loadData = useCallback(async () => {
    if (!selectedEvent?.id) return;
    setLoading(true);
    setError(null);
    try {
      // Load all registrations, including cancelled rows so their assigned
      // physical bibs are included so cancelled assignments can be released
      // and active assignments can be checked against the complete event.
      const allData = await getBibAssignments(selectedEvent.id, undefined);
      setAllRows(allData);

      const assignableData = allData.filter(isAssignableRow);
      // The separate effect below applies the current class filter after
      // allRows has been updated. This keeps data loading independent of UI
      // filtering and avoids reloading Firebase data when a filter changes.
      setRows(assignableData);
      
      // Initialise edit state from all loaded bibs (so we don't lose data when filtering)
      const initial: Record<string, string> = {};
      allData.forEach((r) => {
        // Cancelled registrations no longer reserve a physical bib.
        initial[r.registrationId] = isCancelledRow(r) ? '' : (r.bib != null ? String(r.bib) : '');
      });
      setEditedBibs(initial);
    } catch (err) {
      setError('Failed to load registrations: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent?.id]);

  // Load data when component mounts or when selection criteria change
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Apply class filter when selected classes change (without reloading from Firebase)
  useEffect(() => {
    if (allRows.length > 0) {
      const assignableData = allRows.filter(isAssignableRow);
      const filteredData = selectedClasses.size > 0
        ? assignableData.filter((r) => selectedClasses.has(r.raceDistance))
        : assignableData;
      setRows(filteredData);
    }
  }, [selectedClasses, allRows]);

  const handleBibChange = (registrationId: string, value: string) => {
    setEditedBibs((prev) => ({ ...prev, [registrationId]: value }));
    setSuccessMsg(null);
  };

  const handleClassToggle = (classId: string) => {
    setSelectedClasses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(classId)) {
        newSet.delete(classId);
      } else {
        newSet.add(classId);
      }
      return newSet;
    });
  };

  const handleSelectAllClasses = () => {
    setSelectedClasses(new Set(availableClasses));
  };

  const handleDeselectAllClasses = () => {
    setSelectedClasses(new Set());
  };

  const handleAutoAssign = () => {
    const start = parseInt(startBib, 10);
    if (isNaN(start) || start < 1) {
      setError('Start bib must be a positive number');
      return;
    }
    const assigned = autoAssignBibs(rows, start);
    const updated: Record<string, string> = { ...editedBibs };
    assigned.forEach((r) => {
      updated[r.registrationId] = r.bib != null ? String(r.bib) : '';
    });
    setEditedBibs(updated);
    setSuccessMsg(null);
    setError(null);
  };

  const handleClearAll = () => {
    const cleared: Record<string, string> = { ...editedBibs };
    rows.forEach((r) => { cleared[r.registrationId] = ''; });
    setEditedBibs(cleared);
    setSuccessMsg(null);
  };

  const handleSave = async () => {
    if (invalidBibs.size > 0 || duplicateBibs.size > 0) {
      setError('Resolve invalid or duplicate bib numbers before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const assignments = allRows.map((r) => {
        const raw = editedBibs[r.registrationId] ?? '';
        const num = isCancelledRow(r) ? null : parseBibValue(raw);
        return {
          registrationId: r.registrationId,
          bib: num,
        };
      });
      await saveBibAssignments(selectedEvent!.id, assignments);
      setSuccessMsg(`Saved bib assignments for ${assignments.length} registrations.`);
      await loadData();
    } catch (err) {
      setError('Failed to save: ' + (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const activeRowsDirty = rows.some((r) => {
    const current = editedBibs[r.registrationId] ?? '';
    const original = r.bib != null ? String(r.bib) : '';
    return current !== original;
  });
  const cancelledBibsToClear = allRows.filter((r) => isCancelledRow(r) && r.bib != null).length;
  const isDirty = activeRowsDirty || cancelledBibsToClear > 0;

  const invalidBibs = useMemo(() => {
    const invalid = new Set<string>();
    allRows.forEach((r) => {
      if (!isAssignableRow(r)) return;
      const raw = editedBibs[r.registrationId] ?? '';
      if (raw.trim() !== '' && parseBibValue(raw) === null) invalid.add(r.registrationId);
    });
    return invalid;
  }, [allRows, editedBibs]);

  const duplicateBibs = useMemo(() => {
    const seen = new Map<number, number>();
    allRows.forEach((r) => {
      if (!isAssignableRow(r)) return;
      const raw = editedBibs[r.registrationId] ?? '';
      const bib = parseBibValue(raw);
      if (bib !== null) seen.set(bib, (seen.get(bib) ?? 0) + 1);
    });
    const dupes = new Set<number>();
    seen.forEach((count, bib) => { if (count > 1) dupes.add(bib); });
    return dupes;
  }, [allRows, editedBibs]);

  if (!selectedEvent?.id) {
    return <Alert severity="info">Select an event edition to manage bib assignments.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Bib Assignments — {selectedEvent.id}</Typography>
        <Tooltip title="Reload">
          <IconButton onClick={loadData} disabled={loading}>
            <RefreshIcon size={18} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}
      {duplicateBibs.size > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Duplicate bib numbers: {Array.from(duplicateBibs).sort((a, b) => a - b).join(', ')}
        </Alert>
      )}
      {invalidBibs.size > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Bib numbers must be positive whole numbers.
        </Alert>
      )}
      {cancelledBibsToClear > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {cancelledBibsToClear} bib assignment(s) on cancelled registrations will be released when saving.
        </Alert>
      )}

      {/* Class filter controls */}
      {availableClasses.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Filter by class</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={handleSelectAllClasses} disabled={loading}>
                Select All
              </Button>
              <Button size="small" onClick={handleDeselectAllClasses} disabled={loading}>
                Deselect All
              </Button>
            </Box>
          </Box>
          <FormGroup sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}>
            {availableClasses.map((classId) => (
              <FormControlLabel
                key={classId}
                control={
                  <Checkbox
                    checked={selectedClasses.has(classId)}
                    onChange={() => handleClassToggle(classId)}
                    size="small"
                    disabled={loading}
                  />
                }
                label={getClassLabel(classId)}
              />
            ))}
          </FormGroup>
        </Paper>
      )}

      {/* Auto-assign controls */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Auto-assign sequential bibs</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Starting bib #"
            value={startBib}
            onChange={(e) => setStartBib(e.target.value)}
            type="number"
            size="small"
            sx={{ width: 140 }}
            inputProps={{ min: 1 }}
          />
          <Button
            variant="outlined"
            startIcon={<AutoIcon size={16} />}
            onClick={handleAutoAssign}
            disabled={loading}
          >
            Auto-assign
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<ClearIcon size={16} />}
            onClick={handleClearAll}
            disabled={loading}
          >
            Clear all
          </Button>
          <Typography variant="caption" color="text.secondary">
            Assigns bibs sequentially sorted by registration number, starting at the given number.
          </Typography>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Class</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reg #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Bib</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => {
                  const bibValue = editedBibs[row.registrationId] ?? '';
                  const bibNum = parseBibValue(bibValue);
                  const isDupe = bibNum !== null && duplicateBibs.has(bibNum);
                  return (
                    <TableRow key={row.registrationId} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        {row.firstName} {row.lastName}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getClassLabel(row.raceDistance)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{row.registrationNumber ?? '—'}</TableCell>
                      <TableCell>
                        <TextField
                          value={bibValue}
                          onChange={(e) => handleBibChange(row.registrationId, e.target.value)}
                          type="number"
                          size="small"
                          sx={{ width: 90 }}
                          inputProps={{ min: 1 }}
                          error={isDupe}
                          placeholder="—"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                      No confirmed/pending registrations found for this edition.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon size={16} />}
              onClick={handleSave}
              disabled={saving || !isDirty || duplicateBibs.size > 0 || invalidBibs.size > 0}
            >
              {saving ? 'Saving…' : 'Save bib assignments'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BibAssignmentPanel;
